import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Crown,
    ArrowRight,
    Check,
    Shield,
    PenTool,
    Sparkles,
    Loader2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import useSeo from "../lib/useSeo";

export default function PublishPage() {
    useSeo({
        title: "Publish With Us",
        description:
            "Publish with EIGOO Books and keep your IP. Writers earn 50% to 60% of every sale. Three tiers: Starter (free), Pro, Premier.",
    });

    const [packages, setPackages] = useState([]);
    const [loadingId, setLoadingId] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/publishing/packages").then(({ data }) => setPackages(data));
    }, []);

    const start = async (pkg) => {
        setError("");
        setLoadingId(pkg.package_id);
        try {
            if (pkg.amount === 0) {
                // Free Starter — route them to apply.
                window.location.href = "/apply?tier=starter";
                return;
            }
            const { data } = await api.post("/create-checkout-session", {
                package_id: pkg.package_id,
                origin_url: window.location.origin,
            });
            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                setError("Unable to open checkout. Please try again.");
                setLoadingId(null);
            }
        } catch (err) {
            setError(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    err?.message ||
                    "Something went wrong."
            );
            setLoadingId(null);
        }
    };

    return (
        <main data-testid="publish-page" className="text-white">
            {/* HEADER */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 spotlight" />
                <div className="relative max-w-5xl mx-auto px-6 lg:px-10 pt-24 pb-16 text-center">
                    <div className="eyebrow mb-5 inline-flex items-center gap-2">
                        <PenTool className="w-3.5 h-3.5" /> For Writers
                    </div>
                    <h1 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">
                        Publish. Earn.{" "}
                        <span className="text-gold-gradient italic">Own your story.</span>
                    </h1>
                    <p className="text-[#cfcfcf] text-lg mt-8 max-w-2xl mx-auto leading-relaxed">
                        EIGOO Books is a publishing company for writers who want distribution and
                        marketing without surrendering their work. You keep your copyright. You
                        keep your audience. We handle the hard parts.
                    </p>
                </div>
            </section>

            {/* DATA OWNERSHIP STATEMENT */}
            <section
                data-testid="data-ownership-section"
                className="max-w-5xl mx-auto px-6 lg:px-10 pb-16"
            >
                <div className="frame-gold p-8 md:p-10 grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-2 flex md:justify-center">
                        <div className="w-16 h-16 border border-[#D4AF37]/50 rounded-full flex items-center justify-center">
                            <Shield className="w-7 h-7 text-[#D4AF37]" />
                        </div>
                    </div>
                    <div className="md:col-span-10">
                        <div className="eyebrow mb-2">The EIGOO promise</div>
                        <h2 className="font-serif text-2xl md:text-3xl text-white leading-snug">
                            You own your work.{" "}
                            <span className="text-[#E6D59A] italic">EIGOO Books</span> handles
                            publishing, distribution, and marketing. Your IP stays yours.
                        </h2>
                        <p className="text-[#A1A1AA] text-sm mt-3 leading-relaxed">
                            No rights grab. No perpetual contracts. You can take your titles with you
                            at any time. We earn when you earn — our incentives are aligned with yours.
                        </p>
                    </div>
                </div>
            </section>

            {/* THREE TIERS */}
            <section
                id="tiers"
                data-testid="pricing-tiers"
                className="max-w-7xl mx-auto px-6 lg:px-10 pb-24"
            >
                <div className="text-center mb-14">
                    <div className="eyebrow mb-3">Publishing tiers</div>
                    <h2 className="font-serif text-4xl md:text-5xl">
                        Pick the shelf your work deserves.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {packages.map((pkg, i) => (
                        <PricingCard
                            key={pkg.package_id}
                            pkg={pkg}
                            index={i}
                            loading={loadingId === pkg.package_id}
                            onStart={() => start(pkg)}
                        />
                    ))}
                </div>

                {error && (
                    <p
                        data-testid="publish-error"
                        className="mt-6 text-center text-[#cd6a6a] text-xs uppercase tracking-widest"
                    >
                        {error}
                    </p>
                )}
            </section>

            {/* HOW IT WORKS */}
            <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
                <div className="eyebrow mb-3 text-center">How it works</div>
                <h2 className="font-serif text-3xl md:text-4xl text-center mb-14">
                    From manuscript to <span className="text-gold-gradient italic">readership</span>.
                </h2>
                <div className="grid md:grid-cols-3 gap-10">
                    {[
                        {
                            n: "I",
                            t: "Apply",
                            d: "Tell us about your work. Share a sample. We respond within 5 business days.",
                        },
                        {
                            n: "II",
                            t: "Choose a tier",
                            d: "Pick Starter (free), Pro, or Premier. Tier can change as your audience grows.",
                        },
                        {
                            n: "III",
                            t: "Publish & earn",
                            d: "We handle production, listing, and marketing. You keep 50–60% of every sale.",
                        },
                    ].map((s) => (
                        <div key={s.n} className="relative">
                            <div className="font-serif text-6xl text-[#D4AF37]/40 italic mb-3">{s.n}</div>
                            <h3 className="font-serif text-2xl text-white mb-2">{s.t}</h3>
                            <p className="text-[#A1A1AA] text-sm leading-relaxed">{s.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-28 text-center">
                <Sparkles className="w-6 h-6 text-[#D4AF37] mx-auto mb-6" />
                <h3 className="font-serif text-3xl md:text-5xl leading-tight">
                    Ready to put your name on an{" "}
                    <span className="text-gold-gradient italic">EIGOO imprint</span>?
                </h3>
                <Link
                    to="/apply"
                    data-testid="publish-apply-cta"
                    className="btn-gold mt-10 inline-flex"
                >
                    Submit your application <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            </section>
        </main>
    );
}

function PricingCard({ pkg, index, loading, onStart }) {
    const isPro = pkg.package_id === "publish_pro";
    const isPremier = pkg.package_id === "publish_premier";
    const isFree = pkg.amount === 0;

    const borderColor = isPremier
        ? "rgba(212,175,55,0.7)"
        : isPro
          ? "rgba(212,175,55,0.4)"
          : "rgba(212,175,55,0.18)";

    const tierLabel = pkg.name.split(" — ")[0];

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            data-testid={`pricing-card-${pkg.package_id}`}
            className="relative frame-gold p-8 flex flex-col"
            style={{ borderColor }}
        >
            {isPremier && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#D4AF37] text-[#0a0a0a] text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Flagship
                </div>
            )}
            <div className="eyebrow mb-3">{tierLabel}</div>
            <div className="flex items-baseline gap-2 mb-1">
                <span className="font-serif text-5xl text-gold-gradient">
                    {isFree ? "Free" : `$${Math.round(pkg.amount)}`}
                </span>
                {!isFree && (
                    <span className="text-[#A1A1AA] text-xs uppercase tracking-widest">
                        / month
                    </span>
                )}
            </div>
            <div className="font-serif italic text-[#E6D59A] text-xl mb-6">
                {Math.round((pkg.split_pct || 0) * 100)}% royalty
            </div>
            <div className="gold-rule mb-6" />
            <ul className="space-y-3 text-sm text-[#cfcfcf] flex-1 mb-8">
                {pkg.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
            <button
                data-testid={`pricing-cta-${pkg.package_id}`}
                onClick={onStart}
                disabled={loading}
                className={`${isPro || isPremier ? "btn-gold" : "btn-ghost-gold"} w-full justify-center disabled:opacity-60`}
            >
                {loading ? (
                    <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Opening checkout…
                    </>
                ) : isFree ? (
                    <>
                        Start free <ArrowRight className="w-3.5 h-3.5" />
                    </>
                ) : (
                    <>
                        Choose {tierLabel.split(" ")[0]} <ArrowRight className="w-3.5 h-3.5" />
                    </>
                )}
            </button>
            {pkg.recurring_hint && (
                <p className="mt-3 text-[10px] uppercase tracking-widest text-[#5a5a5a] text-center">
                    {pkg.recurring_hint}
                </p>
            )}
        </motion.div>
    );
}
